import { useEffect, useRef } from "react"
import { ScrambleTextOnHover } from "@/components/scramble-text"
import { AnimatedNoise } from "@/components/animated-noise"
import { BitmapChevron } from "@/components/bitmap-chevron"
import { handleSectionLinkClick } from "@/lib/scroll-to-section"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

export function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!sectionRef.current || !contentRef.current) return

    const ctx = gsap.context(() => {
      gsap.to(contentRef.current, {
        y: -100,
        opacity: 0,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      })
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section ref={sectionRef} id="hero" className="relative flex min-h-screen items-center px-6 md:px-12">
      <AnimatedNoise opacity={0.03} />

      {/* Left vertical labels */}
      <div className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground -rotate-90 origin-left block whitespace-nowrap">
          
        </span>
      </div>

      {/* Main content */}
      <div ref={contentRef} className="flex w-full flex-1 flex-col items-center text-center">
        <h1 className="sr-only">AEXIOM</h1>
        <div className="mx-auto w-full max-w-[1100px] px-4 sm:px-0">
          <img
            src="/aexiom-header.png"
            alt="AEXIOM"
            className="mx-auto w-full max-w-full h-auto object-contain"
          />
        </div>

        <h2 className="mt-10 max-w-4xl font-[var(--font-bebas)] text-[clamp(1.75rem,4.5vw,3.75rem)] leading-[1.05] tracking-wide uppercase">
          <span className="text-foreground">Your Dedicated Expert Team </span>

          <br></br>
          <span className="text-accent">For Silicon Success</span>
        </h2>

        <p className="mt-8 max-w-2xl font-mono text-sm leading-relaxed text-muted-foreground md:text-base">
          End-to-end ASIC &amp; SoC physical design — RTL to GDSII — delivered by engineers with 15–20+ years of
          experience across process nodes from 180nm to 5nm.
        </p>

        <div className="mt-12 flex flex-col items-center justify-center gap-6 sm:flex-row sm:gap-10">
          <a
            href="#close"
            onClick={(event) => handleSectionLinkClick(event, "#close")}
            className="group inline-flex items-center gap-3 bg-accent px-6 py-3 font-mono text-xs uppercase tracking-widest text-accent-foreground transition-all duration-200 hover:bg-accent/90"
          >
            <ScrambleTextOnHover text="Book a Design Consultation" as="span" duration={0.6} />
            <BitmapChevron className="transition-transform duration-[400ms] ease-in-out group-hover:rotate-45" />
          </a>
          <a
            href="#work"
            className="font-mono text-xs uppercase tracking-widest text-muted-foreground underline underline-offset-4 transition-colors duration-200 hover:text-foreground"
          >
            See Our Tape-Outs →
          </a>
        </div>
      </div>
    </section>
  )
}
